import { MATERIAL_CATEGORIES } from "../pricing/material-categories";
import { isUnknownRecord } from "../validation/narrow";
import {
  formatConditionLabel,
  humanizeProductValue,
} from "./product-name";

export {
  formatConditionLabel,
  formatLineDisplayName,
  formatListingLineName,
  formatProductDisplayName,
  isChosenListingTitle,
} from "./product-name";
export {
  splitProductSourceLocation,
  stripProductSourceLocation,
} from "./product-source-location";

export interface DimensionsLike {
  length?: number | null;
  width?: number | null;
  height?: number | null;
  unit?: string | null;
  raw?: string | null;
}

export function formatDimensionsLabel(dims: unknown): string | null {
  const value = toDimensionsLike(dims);
  if (!value) return null;
  const parts = [value.length, value.width, value.height].filter(
    (part): part is number => typeof part === "number" && part > 0,
  );
  if (parts.length >= 2) {
    const unit = value.unit ? ` ${value.unit}` : "";
    return `${parts.join(" × ")}${unit}`;
  }
  return value.raw?.trim() || null;
}

function positiveNumber(value: unknown): number | undefined {
  return typeof value === "number" && value > 0 ? value : undefined;
}

function nullableNumber(value: unknown): number | null | undefined {
  return typeof value === "number" || value === null ? value : undefined;
}

function nullableString(value: unknown): string | null | undefined {
  return typeof value === "string" || value === null ? value : undefined;
}

function toDimensionsLike(value: unknown): DimensionsLike | null {
  if (!isUnknownRecord(value)) return null;
  return {
    length: nullableNumber(value.length),
    width: nullableNumber(value.width),
    height: nullableNumber(value.height),
    unit: nullableString(value.unit),
    raw: nullableString(value.raw),
  };
}

export function resolveListingDimensions(
  dimensions: unknown,
  specs: unknown,
): DimensionsLike | null {
  const structured = toDimensionsLike(dimensions);
  if (structured && formatDimensionsLabel(structured)) {
    return structured.unit == null ? { ...structured, unit: "in" } : structured;
  }

  const record = isUnknownRecord(specs) ? specs : null;
  if (record) {
    const fromSpecs: DimensionsLike = {
      length: positiveNumber(record.length),
      width: positiveNumber(record.width),
      height: positiveNumber(record.height),
      unit: typeof record.unit === "string" ? record.unit : "in",
    };
    if (formatDimensionsLabel(fromSpecs)) return fromSpecs;
  }
  return null;
}

const NON_PILL_SPEC_KEYS = new Set([
  "service",
  "length",
  "width",
  "height",
  "depth",
  "dimensions",
  "size",
  "productsize",
  "capacity",
  "unit",
  "capacityunit",
  "raw",
  "condition",
  "producttype",
]);

function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[-_]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const LEADING_SPEC_KEYS = ["heated"];

function specPriority(key: string): number {
  const index = LEADING_SPEC_KEYS.indexOf(key.toLowerCase());
  return index === -1 ? LEADING_SPEC_KEYS.length : index;
}

const SPEC_PILL_FORMATTERS: Record<string, (value: unknown) => string | null> = {
  entry: (value) => (value ? `${value} entry` : null),
  heated: (value) => (value === true ? "Heat treated" : null),
  material: (value) =>
    typeof value === "string" ? humanizeProductValue(value) : null,
  type: (value) =>
    typeof value === "string" ? humanizeProductValue(value) : null,
  style: (value) =>
    typeof value === "string" ? humanizeProductValue(value) : null,
  wall: (value) => (value ? String(value) : null),
  bottoms: (value) =>
    typeof value === "string" ? `${humanizeProductValue(value)} bottom` : null,
};

export function buildSpecPills(
  condition: string | undefined | null,
  specs: unknown,
  max = 4,
): string[] {
  const pills: string[] = [];
  const conditionPill = formatConditionLabel(condition);
  if (conditionPill) pills.push(conditionPill);

  const record = isUnknownRecord(specs) ? specs : null;
  if (record) {
    const entries = Object.entries(record).sort(
      ([left], [right]) => specPriority(left) - specPriority(right),
    );
    for (const [key, value] of entries) {
      if (pills.length >= max) break;
      if (NON_PILL_SPEC_KEYS.has(key.toLowerCase())) continue;
      if (value == null || value === "" || value === false) continue;

      const formatter = SPEC_PILL_FORMATTERS[key.toLowerCase()];
      let pill: string | null;
      if (formatter) {
        pill = formatter(value);
      } else if (typeof value === "boolean") {
        pill = value ? humanizeKey(key) : null;
      } else if (typeof value === "string") {
        pill = humanizeProductValue(value);
      } else if (typeof value === "number") {
        pill = `${humanizeKey(key)}: ${value}`;
      } else {
        pill = null;
      }

      if (pill && !pills.includes(pill)) pills.push(pill);
    }
  }

  return pills.slice(0, max);
}

const EMPTY_SPEC_VALUES = new Set([
  "",
  "other",
  "n/a",
  "na",
  "none",
  "unknown",
]);

export function readSpecValue(specs: unknown, key: string): string | null {
  if (!isUnknownRecord(specs)) return null;
  const record = specs;
  const matchKey = Object.keys(record).find(
    (candidate) => candidate.toLowerCase() === key.toLowerCase(),
  );
  if (matchKey === undefined) return null;
  const value = record[matchKey];
  if (value == null || value === false) return null;
  const text =
    typeof value === "string" ? humanizeProductValue(value) : String(value);
  if (EMPTY_SPEC_VALUES.has(text.trim().toLowerCase())) return null;
  return text;
}

const MATERIAL_LABEL_BY_CATEGORY: Record<string, string> = {
  containerboard: "Cardboard",
  lumber: "Wood",
  plastics: "Plastic",
  steel: "Steel",
};

const SPECIFIC_MATERIAL_BY_TYPE: Record<string, string> = {
  "ibc-totes": "HDPE",
  "bulk-bags": "Polypropylene",
};

export function resolveListingMaterial(
  specs: unknown,
  productTypeSlug?: string | null,
): string | null {
  const fromSpecs = readSpecValue(specs, "material");
  if (fromSpecs) return fromSpecs;
  if (!productTypeSlug) return null;
  if (SPECIFIC_MATERIAL_BY_TYPE[productTypeSlug]) {
    return SPECIFIC_MATERIAL_BY_TYPE[productTypeSlug];
  }
  const category = MATERIAL_CATEGORIES.find((entry) =>
    entry.relatedProductTypes.includes(productTypeSlug),
  );
  return category ? (MATERIAL_LABEL_BY_CATEGORY[category.slug] ?? null) : null;
}

export interface ListingAttribute {
  name: string;
  value: string;
}

const NON_ATTRIBUTE_SPEC_KEYS = new Set([
  ...NON_PILL_SPEC_KEYS,
  "material",
  "color",
  "properties",
  "capacityunit",
  "unit",
]);

const FILE_ID_KEY_PATTERN = /file(id|ids)$/i;

const CAPACITY_UNIT_BY_PRODUCT_TYPE: Record<string, string> = {
  "ibc-totes": "gal",
  "metal-drums": "gal",
  "plastic-drums": "gal",
  "fiber-drums": "gal",
  "bulk-bags": "lbs",
};

export function resolveListingCapacity(
  specs: unknown,
  productTypeSlug?: string | null,
): string | null {
  const record = isUnknownRecord(specs) ? specs : null;
  if (!record) return null;

  const capacityKey = Object.keys(record).find(
    (key) => key.toLowerCase() === "capacity",
  );
  if (capacityKey === undefined) return null;
  const raw = record[capacityKey];
  if (raw == null || raw === "" || raw === false) return null;

  const value =
    typeof raw === "number"
      ? raw.toLocaleString("en-US")
      : humanizeProductValue(String(raw));
  if (EMPTY_SPEC_VALUES.has(value.trim().toLowerCase())) return null;

  const unitKey = Object.keys(record).find(
    (key) => key.toLowerCase() === "capacityunit",
  );
  const unitValue = unitKey === undefined ? undefined : record[unitKey];
  const specUnit = typeof unitValue === "string" ? unitValue : "";
  const valueCarriesUnit = /[a-z]/i.test(value);
  const inferredUnit = productTypeSlug
    ? (CAPACITY_UNIT_BY_PRODUCT_TYPE[productTypeSlug] ?? "")
    : "";
  const unit = specUnit || (valueCarriesUnit ? "" : inferredUnit);
  return unit ? `${value} ${unit}` : value;
}

function readCapacityAttribute(
  record: Record<string, unknown>,
  productTypeSlug?: string | null,
): ListingAttribute | null {
  const value = resolveListingCapacity(record, productTypeSlug);
  return value ? { name: "Capacity", value } : null;
}

export function buildListingAttributes(
  specs: unknown,
  labels?: Record<string, string>,
  productTypeSlug?: string | null,
): ListingAttribute[] {
  const record = isUnknownRecord(specs) ? specs : null;
  if (!record) return [];

  const attributes: ListingAttribute[] = [];
  const capacity = readCapacityAttribute(record, productTypeSlug);
  if (capacity) attributes.push(capacity);

  for (const [key, value] of Object.entries(record)) {
    if (NON_ATTRIBUTE_SPEC_KEYS.has(key.toLowerCase())) continue;
    if (FILE_ID_KEY_PATTERN.test(key)) continue;
    if (value == null || value === "" || value === false) continue;

    const formatter = SPEC_PILL_FORMATTERS[key.toLowerCase()];
    let formatted: string | null;
    if (formatter) {
      formatted = formatter(value);
    } else if (typeof value === "boolean") {
      formatted = value ? "Yes" : null;
    } else if (typeof value === "string") {
      formatted = humanizeProductValue(value);
    } else if (typeof value === "number") {
      formatted = String(value);
    } else {
      formatted = null;
    }

    if (!formatted || EMPTY_SPEC_VALUES.has(formatted.trim().toLowerCase())) {
      continue;
    }
    const name = labels?.[key.toLowerCase()] ?? humanizeKey(key);
    attributes.push({ name, value: formatted });
  }
  return attributes;
}
